'use client';

import type React from 'react';
import { InputText } from 'src/components/InputText';
import { TextArea } from 'src/components/TextArea';
import { Select } from 'src/components/Select';
import { FormRegisterProduct } from 'src/features/createProduct/types/types';
import type { Product } from 'src/model/Product';
import { Package, FileText, Hash, Tag, Activity } from 'lucide-react';

interface BasicInfoProps {
  formData: any;
  formErrors: Partial<Record<keyof Product, string[]>>;
  onChangeFormInput: (inputName: string) => (value: string) => void;
}

const statusOptions = [
  { value: 'ACTIVE', label: 'Ativo' },
  { value: 'INACTIVE', label: 'Inativo' },
  { value: 'DISCONTINUED', label: 'Estoque Zerado' },
];

const BasicInfo: React.FC<BasicInfoProps> = ({
  formData,
  formErrors,
  onChangeFormInput,
}) => {
  const descriptionLength =
    formData[FormRegisterProduct.description]?.length || 0;
  const maxDescriptionLength = 2000;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
            <Package className="h-6 w-6 text-primary" />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            Informações Básicas
          </h2>
        </div>
        <p className="text-muted-foreground text-base ml-[60px]">
          Configure as informações principais do produto
        </p>
      </div>

      {/* Card Principal */}
      <div className="bg-card rounded-2xl border-2 border-border shadow-lg hover:shadow-xl transition-shadow duration-300">
        <div className="p-8 space-y-8">
          {/* Nome do Produto */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <label className="text-lg font-semibold text-foreground">
                  Nome do Produto *
                </label>
                <p className="text-sm text-muted-foreground">
                  Nome que será exibido para os clientes
                </p>
              </div>
            </div>
            <InputText
              name="name"
              placeholder="Ex: Camiseta Premium Algodão"
              value={formData[FormRegisterProduct.name]}
              onChange={(e) =>
                onChangeFormInput(FormRegisterProduct.name)(e.target.value)
              }
              error={formErrors.name && formErrors.name[0]}
              inputClassName="rounded-xl px-4 py-3 w-full bg-background border-2 border-input 
                             focus:border-primary focus:ring-4 focus:ring-primary/10
                             transition-all duration-300 outline-none"
            />
          </div>

          {/* Divisor */}
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

          {/* Descrição */}
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <label className="text-lg font-semibold text-foreground">
                    Descrição *
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Descreva detalhes, características e benefícios do produto
                  </p>
                </div>
              </div>
              <div className="text-sm text-muted-foreground font-medium px-3 py-1 rounded-full bg-muted">
                {descriptionLength}/{maxDescriptionLength}
              </div>
            </div>
            <TextArea
              name="description"
              placeholder="Descreva o produto de forma detalhada..."
              value={formData[FormRegisterProduct.description] ?? ''}
              maxLength={maxDescriptionLength}
              onChange={(e) =>
                onChangeFormInput(FormRegisterProduct.description)(
                  e.target.value
                )
              }
              error={formErrors.description && formErrors.description[0]}
              textAreaClassName="rounded-xl px-4 py-3 w-full bg-background border-2 border-input
                             focus:border-primary focus:ring-4 focus:ring-primary/10
                             transition-all duration-300 outline-none min-h-[120px] resize-y"
            />
          </div>

          {/* Divisor */}
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

          {/* SKU e Marca - Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Hash className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <label className="text-lg font-semibold text-foreground">
                    SKU *
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Código único de identificação
                  </p>
                </div>
              </div>
              <InputText
                name="sku"
                placeholder="Ex: CAM-001-AZ-M"
                value={formData[FormRegisterProduct.sku]}
                onChange={(e) =>
                  onChangeFormInput(FormRegisterProduct.sku)(e.target.value)
                }
                error={formErrors.sku && formErrors.sku[0]}
                inputClassName="rounded-xl px-4 py-3 w-full bg-background border-2 border-input 
                               focus:border-primary focus:ring-4 focus:ring-primary/10
                               transition-all duration-300 outline-none font-mono text-sm"
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Tag className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <label className="text-lg font-semibold text-foreground">
                    Marca *
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Fabricante ou marca do produto
                  </p>
                </div>
              </div>
              <InputText
                name="brand"
                placeholder="Ex: Nike, Adidas, Samsung"
                value={formData.brand?.name || ''}
                onChange={(e) =>
                  onChangeFormInput('brand')({
                    ...formData.brand,
                    name: e.target.value,
                  })
                }
                error={formErrors.brand && formErrors.brand[0]}
                inputClassName="rounded-xl px-4 py-3 w-full bg-background border-2 border-input 
                               focus:border-primary focus:ring-4 focus:ring-primary/10
                               transition-all duration-300 outline-none"
              />
            </div>
          </div>

          {/* Divisor */}
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

          {/* Status */}
          <div className="space-y-4 max-w-md">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Activity className="h-5 w-5 text-primary" />
              </div>
              <div>
                <label className="text-lg font-semibold text-foreground">
                  Status *
                </label>
                <p className="text-sm text-muted-foreground">
                  Disponibilidade do produto
                </p>
              </div>
            </div>
            <Select
              name="status"
              value={formData[FormRegisterProduct.status]}
              onChange={(e) =>
                onChangeFormInput(FormRegisterProduct.status)(e.target.value)
              }
              error={formErrors.status && formErrors.status[0]}
              options={statusOptions}
              selectClassName="rounded-xl px-4 py-3 w-full bg-background border-2 border-input 
                               focus:border-primary focus:ring-4 focus:ring-primary/10
                               transition-all duration-300 outline-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfo;
